import config from '../../config'
import { AppError } from '../../libraries/errors/AppError'
import fetcher, { streamTextFetcher } from '../fetcher'
import {
  StartInterview,
  InterviewStatus,
  AdvisorConversation,
  AdvisorMessage,
} from './types'

const BASE_URL = `${config.VITE_ZRIVE_API_URL}/api/gpt`

export const startInterview = async (topic: string, numberOfMainQuestions: number = 3, numberOfFollowUpQuestions: number = 2, jobDescription?: string, jobOfferId?: string): Promise<InterviewStatus> => {
  return fetcher(`${BASE_URL}/start-interview`, {
    method: 'POST',
    body: JSON.stringify({ topic, numberOfMainQuestions, numberOfFollowUpQuestions, jobDescription, jobOfferId } as StartInterview)
  })
}

export const askGpt = async function* (messages: AdvisorMessage[], conversationId?: number | null) {
  try {
    const response = await streamTextFetcher(`${BASE_URL}/ask-gpt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify({ messages, conversationId }),
      cache: 'no-store',
      keepalive: true,
    })

    if (!response.body) throw new AppError('No response body')

    // Extract conversation ID from header and store it globally
    const newConversationId = response.headers.get('x-conversation-id')
    const allHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => {
      allHeaders[key] = value
    })

    if (newConversationId) {
      // Export this function so it can be called from the component
      window.dispatchEvent(new CustomEvent('conversation-id-received', {
        detail: { conversationId: parseInt(newConversationId, 10) }
      }))
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const eventChunks = chunk.split('/n/n')

        for (const eventChunk of eventChunks) {
          if (!eventChunk.trim()) continue

            const dataMatch = eventChunk.match(/data: (.+)/)
            if (!dataMatch) continue

            const data = dataMatch[1]

            yield data
        }
      }
    } finally {
      reader.releaseLock()
    }
  } catch (err) {
    throw new AppError('Error asking Advisor AI')
  }
}

export const getAllGptConversations = async (): Promise<AdvisorConversation[]> => {
  try {
    const response = await fetcher(`${BASE_URL}/conversations`, { method: 'GET' })
    return response
  } catch (error: unknown) {
    console.error(`Error fetching all GPT conversations: ${error}`)
    throw new Error((error as Error).message)
  }
}

export const getLastInterview = async (): Promise<InterviewStatus | null> => {
  try {
    const response = await fetcher(`${BASE_URL}/interview`, { method: 'GET' })
    return response
  } catch (error: unknown) {
    console.error(`Error fetching latest interview: ${error}`)
    throw new Error((error as Error).message)
  }
}

export const getLastInterviewForJobOfferId = async (jobOfferId: string, topic: string): Promise<InterviewStatus | null> => {
  try {
    const response = await fetcher(`${BASE_URL}/interview/${topic}/job-offer/${jobOfferId}`, { method: 'GET' })
    return response
  } catch (error: unknown) {
    console.error(`Error fetching latest interview: ${error}`)
    throw new Error((error as Error).message)
  }
}

export const dismissInterview = async (sessionId: string): Promise<void> => {
  await fetcher(`${BASE_URL}/interview/${sessionId}/dismiss`, { method: 'PATCH' })
}

export const getAudioInterviewSocketUrl = async (sessionId: string): Promise<{ wsUrl: string }> => {
  try {
    const response = await fetcher(`${BASE_URL}/interview/${sessionId}/stream/ws`, { method: 'GET' })
    return response
  } catch (error: unknown) {
    console.error(`Error fetching audio interview socket url: ${error}`)
    throw new Error((error as Error).message)
  }
}
